// @ts-nocheck
import { useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { SwapHoriz } from '@mui/icons-material'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { useGetAdminCustomers } from 'src/api/customer'
import { useGetAllTypeServiceStations } from 'src/api/service-station'
import {
  IEquipmentMigrationPreview,
  useExecuteEquipmentMigration,
  usePreviewEquipmentMigration,
} from 'src/api/equipment-migration'
import { ICustomer } from 'src/generated-types/customer'
import { IServiceStation } from 'src/generated-types/service-station'

export const EquipmentMigrationScreen = () => {
  const { t } = useTranslation('equipment-migration')
  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer | null>(null)
  const [sourceStation, setSourceStation] = useState<IServiceStation | null>(null)
  const [targetStation, setTargetStation] = useState<IServiceStation | null>(null)
  const [preview, setPreview] = useState<IEquipmentMigrationPreview | null>(null)
  const [migrationDone, setMigrationDone] = useState(false)

  const { data: customersData } = useGetAdminCustomers(0, 1000)
  const customers = customersData?.items ?? []

  // Filter customers to only those that are not global service station providers for source customers
  const sourceCustomers = useMemo(() => customers.filter(c => !c.isGlobalServiceStationProvider), [customers])

  // Find customers that are global service station providers
  const globalProviderCustomers = useMemo(() => customers.filter(c => c.isGlobalServiceStationProvider), [customers])

  const { data: customerStations } = useGetAllTypeServiceStations(selectedCustomer?.id ?? 0)

  const [selectedGlobalCustomer, setSelectedGlobalCustomer] = useState<ICustomer | null>(null)
  const { data: globalStations } = useGetAllTypeServiceStations(selectedGlobalCustomer?.id ?? 0)

  const previewMutation = usePreviewEquipmentMigration()
  const executeMutation = useExecuteEquipmentMigration()

  const handlePreview = () => {
    if (!sourceStation || !targetStation) return
    setPreview(null)
    setMigrationDone(false)
    previewMutation.mutate(
      {
        sourceServiceStationId: sourceStation.id,
        targetServiceStationId: targetStation.id,
      },
      {
        onSuccess: response => {
          setPreview(response.data as unknown as IEquipmentMigrationPreview)
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.message ?? t('preview-failed'))
        },
      },
    )
  }

  const handleExecute = () => {
    if (!sourceStation || !targetStation) return
    executeMutation.mutate(
      {
        sourceServiceStationId: sourceStation.id,
        targetServiceStationId: targetStation.id,
      },
      {
        onSuccess: (response: any) => {
          const result = response.data
          toast.success(
            t('migration-result', {
              migrated: result.migratedCount,
              impregnations: result.impregnationRecordsUpdated,
              deviations: result.deviationsUpdated,
            }),
          )
          setMigrationDone(true)
          setPreview(null)
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.message ?? t('migration-failed'))
        },
      },
    )
  }

  const handleReset = () => {
    setSelectedCustomer(null)
    setSourceStation(null)
    setTargetStation(null)
    setSelectedGlobalCustomer(null)
    setPreview(null)
    setMigrationDone(false)
  }

  return (
    <Box className="p-6" maxWidth={1000}>
      <Typography variant="h5" className="mb-4" fontWeight="bold">
        {t('title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" className="mb-6">
        {t('description')}
      </Typography>

      <Stack spacing={3}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" className="mb-3">
              {t('select-source')}
            </Typography>
            <Stack spacing={2}>
              <Autocomplete
                value={selectedCustomer}
                onChange={(_, value) => {
                  setSelectedCustomer(value)
                  setSourceStation(null)
                  setPreview(null)
                }}
                options={sourceCustomers}
                getOptionLabel={(option: ICustomer) => option.name}
                renderInput={params => <TextField {...params} label={t('customer')} size="small" />}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
              <Autocomplete
                value={sourceStation}
                onChange={(_, value) => {
                  setSourceStation(value)
                  setPreview(null)
                }}
                options={customerStations?.items ?? []}
                getOptionLabel={(option: IServiceStation) =>
                  `${option.name}${option.serviceStationNumber ? ` (${option.serviceStationNumber})` : ''}`
                }
                renderInput={params => <TextField {...params} label={t('source-station')} size="small" />}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={!selectedCustomer}
              />
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" className="mb-3">
              {t('select-target')}
            </Typography>
            <Stack spacing={2}>
              <Autocomplete
                value={selectedGlobalCustomer}
                onChange={(_, value) => {
                  setSelectedGlobalCustomer(value)
                  setTargetStation(null)
                  setPreview(null)
                }}
                options={globalProviderCustomers}
                getOptionLabel={(option: ICustomer) => option.name}
                renderInput={params => <TextField {...params} label={t('global-provider')} size="small" />}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
              <Autocomplete
                value={targetStation}
                onChange={(_, value) => {
                  setTargetStation(value)
                  setPreview(null)
                }}
                options={globalStations?.items ?? []}
                getOptionLabel={(option: IServiceStation) =>
                  `${option.name}${option.serviceStationNumber ? ` (${option.serviceStationNumber})` : ''}`
                }
                renderInput={params => <TextField {...params} label={t('target-station')} size="small" />}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={!selectedGlobalCustomer}
              />
            </Stack>
          </CardContent>
        </Card>

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            onClick={handlePreview}
            disabled={!sourceStation || !targetStation || previewMutation.isLoading}
            startIcon={previewMutation.isLoading ? <CircularProgress size={16} /> : <SwapHoriz />}
          >
            {t('preview-migration')}
          </Button>
          <Button variant="outlined" onClick={handleReset}>
            {t('reset')}
          </Button>
        </Stack>

        {migrationDone && <Alert severity="success">{t('migration-success')}</Alert>}

        {preview && (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" className="mb-2">
                {t('migration-preview')}
              </Typography>

              <Stack spacing={1} className="mb-4">
                <Typography variant="body2">
                  <strong>{t('from')}:</strong> {preview.sourceServiceStationName} ({preview.sourceCustomerName})
                </Typography>
                <Typography variant="body2">
                  <strong>{t('to')}:</strong> {preview.targetServiceStationName} ({preview.targetCustomerName})
                </Typography>
                <Stack direction="row" spacing={1} className="mt-2">
                  <Chip label={`${preview.equipmentToMigrateCount} ${t('equipment')}`} color="primary" size="small" />
                  <Chip label={`${preview.impregnationRecordsToUpdate} ${t('impregnation-records')}`} size="small" />
                  <Chip label={`${preview.activeDeviationsToUpdate} ${t('open-deviations')}`} size="small" />
                </Stack>
              </Stack>

              {preview.equipmentToMigrate.length > 0 && (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('id')}</TableCell>
                        <TableCell>{t('name')}</TableCell>
                        <TableCell>{t('serial-number')}</TableCell>
                        <TableCell>{t('status')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.equipmentToMigrate.map(eq => (
                        <TableRow key={eq.id}>
                          <TableCell>{eq.id}</TableCell>
                          <TableCell>{eq.name}</TableCell>
                          <TableCell>{eq.serialNumber}</TableCell>
                          <TableCell>
                            <Chip label={eq.status} size="small" variant="outlined" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {preview.equipmentToMigrateCount === 0 && <Alert severity="info">{t('no-equipment')}</Alert>}

              {preview.equipmentToMigrateCount > 0 && (
                <Stack direction="row" spacing={2} className="mt-4">
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleExecute}
                    disabled={executeMutation.isLoading}
                    startIcon={executeMutation.isLoading ? <CircularProgress size={16} /> : undefined}
                  >
                    {t('execute-migration')} ({preview.equipmentToMigrateCount} {t('equipment')})
                  </Button>
                </Stack>
              )}
            </CardContent>
          </Card>
        )}
      </Stack>
    </Box>
  )
}

export default EquipmentMigrationScreen
